import { parseApiResponse } from '@/shared/utils/apiResponse';
import { request } from '@/shared/utils/requestBuilder';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';
import { negotiationReportsApiMode } from '../config';
import { demoGateway } from '../demo/gateway';
import {
  briefSchema,
  jobSchema,
  planSchema,
  preferencesSchema,
  snapshotSchema,
  type ReportsGateway,
} from '../types';

const base = '/negociacao/relatorios';
async function call(
  scope: string,
  path: string,
  method: 'GET' | 'POST' | 'PUT',
  body?: object,
  id?: string,
) {
  if (getStoredWorkspaceId() !== scope) throw new Error('A organização mudou. Reabra o relatório.');
  return parseApiResponse<unknown>(
    await request({
      endpoint: `${base}${path}`,
      method,
      body,
      headers: { 'X-Workspace-Id': scope, ...(id ? { 'Idempotency-Key': id } : {}) },
      recuperarWorkspaceNo403: false,
    }),
  );
}
/** Contrato proposto; habilitar api somente após implementar os endpoints no core. */
export const httpGateway: ReportsGateway = {
  async interpret(scope, text, currentBrief) {
    return planSchema.parse(await call(scope, '/interpretar', 'POST', { text, currentBrief }));
  },
  async load(scope) {
    return snapshotSchema.parse(await call(scope, '', 'GET'));
  },
  async create(scope, brief, id) {
    return jobSchema.parse(await call(scope, '', 'POST', briefSchema.parse(brief), id));
  },
  async get(scope, id) {
    return jobSchema.parse(await call(scope, `/${encodeURIComponent(id)}`, 'GET'));
  },
  async cancel(scope, id) {
    return jobSchema.parse(await call(scope, `/${encodeURIComponent(id)}/cancelar`, 'POST'));
  },
  async preferences(scope, preferences) {
    return preferencesSchema.parse(await call(scope, '/preferencias', 'PUT', preferences));
  },
};
export const reportsGateway = negotiationReportsApiMode() ? httpGateway : demoGateway;
export async function downloadReportPdf(scope: string, id: string): Promise<Blob> {
  if (getStoredWorkspaceId() !== scope) throw new Error('A organização mudou. Reabra o relatório.');
  const response = await request({
    endpoint: `${base}/${encodeURIComponent(id)}/pdf`,
    method: 'GET',
    responseType: 'blob',
    headers: { 'X-Workspace-Id': scope },
    recuperarWorkspaceNo403: false,
  });
  if (!response.ok) throw new Error('Não foi possível baixar o PDF. Tente novamente.');
  const blob = await response.blob();
  if (!blob.type.includes('application/pdf'))
    throw new Error('O servidor não retornou um PDF válido.');
  return blob;
}
